import { initializeDB } from '../config/db';
import { TEMP_EXHIBIT_B_STORE, URL } from '../config/constants';
import { v4 as uuidv4 } from 'uuid';
import axiosInstance from '../axiosInstance';

class TempExhibitBService {
    // Save or update a form
    async saveForm(formData) {
        try {
            const db = await initializeDB();
            const tx = db.transaction(TEMP_EXHIBIT_B_STORE, 'readwrite');
            const store = tx.objectStore(TEMP_EXHIBIT_B_STORE);
            
            let data;
            
            if (formData.id) {
                // Update existing form
                const existing = await store.get(formData.id);
                if (existing) {
                    data = {
                        ...existing,
                        ...formData,
                        updatedAt: new Date().toISOString(),
                        syncStatus: formData.syncStatus || existing.syncStatus || 'pending'
                    };
                    await store.put(data);
                } else {
                    // ID doesn't exist, create new with auto-generated ID
                    const { id, ...formDataWithoutId } = formData;
                    data = {
                        ...formDataWithoutId,
                        updatedAt: new Date().toISOString(),
                        createdAt: new Date().toISOString(),
                        uuid: uuidv4(),
                        status: formData.status || 'draft',
                        syncStatus: formData.syncStatus || 'pending'
                    };
                    const newId = await store.add(data);
                    data.id = newId;
                }
            } else {
                // Create new form - remove id field completely for auto-generation
                const { id, ...formDataWithoutId } = formData;
                data = {
                    ...formDataWithoutId,
                    updatedAt: new Date().toISOString(),
                    createdAt: new Date().toISOString(),
                    uuid: uuidv4(),
                    status: formData.status || 'draft',
                    syncStatus: formData.syncStatus || 'pending'
                };
                const newId = await store.add(data);
                data.id = newId;
            }
            
            await tx.done;
            return { success: true, data };
        } catch (error) {
            console.error('Error saving TempExhibitB form:', error);
            return { success: false, error: error.message };
        }
    }

    // Get all forms
    async getAllForms() {
        try {
            const db = await initializeDB();
            const tx = db.transaction(TEMP_EXHIBIT_B_STORE, 'readonly');
            const store = tx.objectStore(TEMP_EXHIBIT_B_STORE);
            const forms = await store.getAll();
            
            // Sort by createdAt descending
            forms.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            
            return forms;
        } catch (error) {
            console.error('Error getting TempExhibitB forms:', error);
            return [];
        }
    }

    // Get form by ID
    async getFormById(id) {
        try {
            const db = await initializeDB();
            const tx = db.transaction(TEMP_EXHIBIT_B_STORE, 'readonly');
            const store = tx.objectStore(TEMP_EXHIBIT_B_STORE);
            const form = await store.get(id);
            return form;
        } catch (error) {
            console.error('Error getting TempExhibitB form by ID:', error);
            return null;
        }
    }

    // Delete form
    async deleteForm(id) {
        try {
            const db = await initializeDB();
            const tx = db.transaction(TEMP_EXHIBIT_B_STORE, 'readwrite');
            const store = tx.objectStore(TEMP_EXHIBIT_B_STORE);
            await store.delete(id);
            await tx.done;
            return { success: true };
        } catch (error) {
            console.error('Error deleting TempExhibitB form:', error);
            return { success: false, error: error.message };
        }
    }

    // Get forms by status
    async getFormsByStatus(status) {
        try {
            const db = await initializeDB();
            const tx = db.transaction(TEMP_EXHIBIT_B_STORE, 'readonly');
            const store = tx.objectStore(TEMP_EXHIBIT_B_STORE);
            const index = store.index('status');
            const forms = await index.getAll(status);
            
            // Sort by createdAt descending
            forms.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            
            return forms;
        } catch (error) {
            console.error('Error getting TempExhibitB forms by status:', error);
            return [];
        }
    }

    // Update form status
    async updateFormStatus(id, status) {
        try {
            const form = await this.getFormById(id);
            if (!form) {
                return { success: false, error: 'Form not found' };
            }
            
            form.status = status;
            form.updatedAt = new Date().toISOString();
            
            if (status === 'completed') {
                form.completedAt = new Date().toISOString();
            }
            
            const db = await initializeDB();
            const tx = db.transaction(TEMP_EXHIBIT_B_STORE, 'readwrite');
            const store = tx.objectStore(TEMP_EXHIBIT_B_STORE);
            await store.put(form);
            await tx.done;
            
            return { success: true, data: form };
        } catch (error) {
            console.error('Error updating TempExhibitB form status:', error);
            return { success: false, error: error.message };
        }
    }

    // Clear all forms (use with caution)
    async clearAllForms() {
        try {
            const db = await initializeDB();
            const tx = db.transaction(TEMP_EXHIBIT_B_STORE, 'readwrite');
            const store = tx.objectStore(TEMP_EXHIBIT_B_STORE);
            await store.clear();
            await tx.done;
            return { success: true };
        } catch (error) {
            console.error('Error clearing TempExhibitB forms:', error);
            return { success: false, error: error.message };
        }
    }

    // Submit form to server
    async submitToServer(formData) {
        try {
            // Prepare data for server (only send needed fields)
            const dataToSend = {
                language: formData.language,
                agencyName: formData.agencyName,
                employeeName: formData.employeeName,
                signature: formData.signature,
                employeeId: formData.employeeId || localStorage.getItem('employeeId') || null
            };

            const response = await axiosInstance.post('/temp-exhibit-b/submit', dataToSend);
            
            if (response.data.success) {
                // Update local form with server ID
                if (formData.id) {
                    const updatedForm = {
                        ...formData,
                        serverId: response.data.id,
                        syncStatus: 'synced',
                        syncedAt: new Date().toISOString(),
                        syncError: null
                    };
                    await this.saveForm(updatedForm);
                }
                
                return { 
                    success: true, 
                    serverId: response.data.id,
                    message: response.data.message 
                };
            }
            
            return { success: false, error: 'Server returned error' };
        } catch (error) {
            console.error('Error submitting TempExhibitB to server:', error);
            
            // Update form with failed sync status
            if (formData.id) {
                const updatedForm = {
                    ...formData,
                    syncStatus: 'failed',
                    syncError: error.response?.data?.message || error.message,
                    lastSyncAttempt: new Date().toISOString()
                };
                await this.saveForm(updatedForm);
            }
            
            return { 
                success: false, 
                error: error.response?.data?.message || error.message 
            };
        }
    }

    // Retry submitting form to server
    async retrySubmit(formId) {
        try {
            const form = await this.getFormById(formId);
            if (!form) {
                return { success: false, error: 'Form not found' };
            }

            if (form.syncStatus === 'synced') {
                return { success: true, message: 'Form already synced' };
            }

            // Update status to indicate retry in progress
            await this.saveForm({
                ...form,
                syncStatus: 'retrying'
            });

            // Try to submit to server
            const result = await this.submitToServer(form);
            
            if (!result.success) {
                // Update with failed status if submit failed
                await this.saveForm({
                    ...form,
                    syncStatus: 'failed',
                    syncError: result.error,
                    lastSyncAttempt: new Date().toISOString()
                });
            }
            
            return result;
        } catch (error) {
            console.error('Error retrying TempExhibitB submission:', error);
            return { 
                success: false, 
                error: error.message 
            };
        }
    }

}

export default new TempExhibitBService();